<template>
  <vetster-slide-over
    ref="addPetSlideOver"
    cancel-label=""
    confirm-close
    container-classes="lg:h-[80vh] lg:max-h-[550px]"
    full-screen
    hide-header
    :show-overlay="showOverlay"
    size="lg"
  >
    <template #header>
      <vetster-slide-over-common-heading
        :header="header"
        :sub-header="subHeader"
        class="px-4 space-y-5"
        @close="back"
      />
    </template>
    <template #content>
      <vetster-pet-form
        ref="petForm"
        container-classes="lg:h-[80vh] lg:max-h-[550px]"
        hide-prefilled-inputs
        :pet="pet"
        :required-inputs="requiredInputs"
        :show-weight="isUpdateForm"
        class="px-standard"
      />
    </template>
    <template #footer>
      <div
        data-toast-anchor
        class="p-4 sm:flex sm:justify-end"
      >
        <vetster-button
          variant="ocean"
          :disabled="loading"
          class="w-full sm:w-1/2 rounded-full"
          @click="handleSavePet"
        >
          {{ buttonLabel }}
        </vetster-button>
      </div>
    </template>
  </vetster-slide-over>
</template>

<script>
import VetsterPetForm from '@/brand-components/VetsterPetForm';
import VetsterSlideOver from '@/brand-components/VetsterSlideOver';
import VetsterSlideOverCommonHeading from '@/brand-components/VetsterSlideOverCommonHeading';
import ParseErrors from '@/mixins/ParseErrors';

export default {
  name: 'VetsterPetSlideOver',

  components: {
    VetsterPetForm,
    VetsterSlideOver,
    VetsterSlideOverCommonHeading,
  },

  mixins: [ParseErrors],

  props: {
    pet: {
      type: Object,
      default: null,
    },
    requiredInputs: {
      type: Array,
      default: () => [],
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    buttonLabel() {
      return this.isUpdateForm ? this.$t('app.continue') : this.$t('app.save');
    },
    header() {
      return this.isUpdateForm
        ? this.$t('pets.review-and-confirm-pets-details', { name: this.pet.name })
        : this.$t('search.lets-meet-your-pet');
    },
    isUpdateForm() {
      return !!this.pet;
    },
    subHeader() {
      return this.isUpdateForm
        ? this.$t('category.update-information-about-pets-will-allow-veterinarian', { name: this.pet.name })
        : null;
    },
  },

  methods: {
    async addPet() {
      try {
        this.loading = true;
        const pet = await this.$store.dispatch('pets/storePet', this.$refs.petForm.getPayload());
        this.$toast(this.$t('pets.add-label-pet', { pet: pet.name }));
        this.$emit('pet-saved', pet);
        this.firePetSavedEvents();
        this.back();
      } catch (e) {
        this.firePetSaveFailedEvents();
        this.showError(e, true);
      } finally {
        this.loading = false;
      }
    },

    back() {
      this.$refs.addPetSlideOver.forceClose();
    },

    firePetSavedEvents() {
      this.$analytics.track({
        eventName: 'add_pet_submit',
        parent: 'guided-search',
        child: 'add-pet',
        event_status: 'SUCCESS',
        version: '2.0',
      });
    },

    firePetSaveFailedEvents() {
      this.$analytics.track({
        eventName: 'add_pet_submit',
        parent: 'guided-search',
        child: 'add-pet',
        event_status: 'FAILURE',
        version: '2.0',
      });
    },

    async handleSavePet() {
      if (this.$refs.petForm.validate()) {
        if (this.isUpdateForm) {
          await this.updatePet();
        } else {
          await this.addPet();
        }
      }
    },

    open() {
      this.$refs.addPetSlideOver.open();
    },

    async updatePet() {
      try {
        this.loading = true;
        const pet = await this.$store.dispatch('pets/modifyPet', {
          uuid: this.pet.uuid,
          ...this.$refs.petForm.getPayload(),
        });

        this.$emit('pet-updated', pet);
        this.back();
      } catch (e) {
        this.showError(e, true);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
